import React, { Component, useRef, useState, useEffect } from 'react';
import {Link, useNavigate} from "react-router-dom";
import NavBarMini from './NavBarMini.js';
import Footer from './Footer.js';
import Announcement from './Announcement';
import LoadingAnimation from './LoadingAnimation';
import Bracket from './Bracket.js';
import GameContainer from './GameContainer';
import * as utils from '../common/Utils.js'
import { getAuth } from "firebase/auth";

import { getDoc, doc } from "firebase/firestore";
import { db } from '../firebase';
import { useLeagueData } from '../contexts/LeagueDataContext.js';
import { useConfig } from '../contexts/ConfigContext.js';

const auth = getAuth();
const user = auth.currentUser
const DATABASE_SOURCE = utils.getDatabaseSource();

const playoffRoundTagNames = ['playin', 'firstround', 'quarterfinals', 'semifinals', 'championship']
const playoffRoundNames = {
  'playin': 'Play-In',
  'firstround': 'First Round',
  'quarterfinals': 'Quarterfinals',
  'semifinals': 'Semifinals',
  'championship': 'Championship',
}
const playoffRoundClasses = {
  'playin': 'round_1_of_5',
  'firstround': 'round_2_of_5',
  'quarterfinals': 'round_3_of_5',
  'semifinals': 'round_4_of_5',
  'championship': 'round_5_of_5',
}
const CURRENT_ROUND = utils.getCurrentPlayoffRound();

// Used for registering swipe actions:
let startOfSwipeX = 0;
let endOfSwipeX = 0;
let startOfSwipeY = 0;
let endOfSwipeY = 0;

const Homepage = () => {
  const navigate = useNavigate();
  const { teams: allTeams, seasonTeams: allSeasonTeams, games: allGames, dataLoaded } = useLeagueData(['teams', 'seasonTeams', 'games']);
  const config = useConfig();
  const SEASON = config.currentSeason;

  const [games, setGames] = useState([])
  const [teams, setTeams] = useState([])
  const [seasonTeams, setSeasonTeams] = useState([])

  const [dataReady, setDataReady] = useState(false);
  const [showOverallStandings, setShowOverallStandings] = useState(true);
  const [seasonData, setSeasonData] = useState({
    leaders: [],
    teams: [],
    upcomingGames: []
  })

  useEffect(() => {
    if (!config.configLoaded) return;
    getSeasonData();
  }, [SEASON, config.configLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!dataLoaded) return;

    const thisSeasonTeams = allSeasonTeams
      .filter(st => st.season == SEASON)
      .map(st => ({ ...st, ...allTeams.find(t => t.id === st.teamID) }));
    setSeasonTeams(thisSeasonTeams);

    setTeams(allTeams.map(t => {
      const st = thisSeasonTeams.find(st => st.teamID === t.id);
      return { ...t, seed: st?.seed || '' };
    }));

    const filtered = allGames
      .filter(g => g.season == parseInt(SEASON) && g.status !== 'pending');
    setGames(filtered);

    setDataReady(true);
  }, [dataLoaded, allTeams, allSeasonTeams, allGames]);

  const getSeasonData = async () => {
    const seasonRef = doc(db, 'seasons', SEASON);
    const seasonSnapshot = await getDoc(seasonRef);
    let seasonData = seasonSnapshot.data();
    if (seasonData){
      seasonData.upcomingGames = seasonData.upcomingGames.map((game) => {
        game.date = game.date.toDate();
        return game;
      });
    } else {
      seasonData = {};
      seasonData.leaders = [];
      seasonData.teams = [];
      seasonData.upcomingGames = [];
    }
    
    setSeasonData(seasonData);
  }

  function displayTeam(team, place) {
    return (
        <tr id={team.id + "-team-row"} key={team.id + "-team-row"}>
          <td className="league-table-data-name user-name no-wrap"><span>{ place + '. ' }</span><a className="unstyled-link" href={"/teams/" + team.id }>{ team.name.toUpperCase() }</a></td>
          <td className="league-table-data desktop-only"><span>{ team.wins }</span></td>
          <td className="league-table-data desktop-only"><span>{ team.losses }</span></td>
          <td className="league-table-data wide-fixed mobile-only"><span>{ team.wins }-{ team.losses }</span></td>
          <td className="league-table-data wide"><span>{ team.winPercentage }</span></td>
          <td className="league-table-data wide desktop-only"><span>{ team.pointsFor }</span></td>
          <td className="league-table-data wide desktop-only"><span>{ team.pointsAgainst }</span></td>
          <td className="league-table-data wide desktop-only"><span>{ team.pointsFor - team.pointsAgainst }</span></td>
          <td className="league-table-data wide-fixed desktop-only"><span>{ team.streak }</span></td>
        </tr>
    );
  }

  function displayTeams (teams) {
    if (!teams){
      return;
    }

    const team_elements = teams.map((team, place) => displayTeam(team, place+1) )

    return team_elements;
  }

  function displayUpcomingGame (game) {
    let gameTBD = false;

    let awayTeam;
    let homeTeam;

    if (game.awayTeamID) {
      awayTeam = utils.getTeamForID(teams, game.awayTeamID);
    } else {
      const placeholderName = game.awayTeamPlaceholder
      awayTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
      gameTBD = true;
    }

    if (game.homeTeamID) {
      homeTeam = utils.getTeamForID(teams, game.homeTeamID);
    } else {
      const placeholderName = game.homeTeamPlaceholder
      homeTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
      gameTBD = true;
    }

    let hours = game.date.getHours();
    let hoursLabel = 'AM'
    if(hours >= 12){
      hoursLabel = 'PM';
    }

    if(hours > 12){
      hours -= 12;
    }
    if(hours == 0){
      hours = 12;
    }

    return (
      <div className={`league-game-container-homepage ${game.status == 'cancelled' ? 'cancelled-game' : ''}`}  onClick={() => {
        navigate(`/games/${game.id}`)
      }} key={game.id + '-game-container'}>
        <p className="league-title-skinny-small" key={game.id + "-date"}>{ awayTeam.name.toUpperCase() } vs. { homeTeam.name.toUpperCase() }</p>
        <p className="league-game-location" key={game.id + "-location"}>{ utils.getLocationName(game.location) }</p>
        <p key={game.id}>{ game.date.toDateString() } @ { hours }:{ game.date.getMinutes() } { hoursLabel }</p>
      </div>
    )
  }

  function anyGamesInLastDay () {
    let today = Date.now();
    let oneDayAgo = today - (1 * 24 * 60 * 60 * 1000);

    let gamesInTheLastDay = games.filter((game) => game.date < today && game.date > oneDayAgo && game.status == 'completed')
    return gamesInTheLastDay.length > 0;
  }

  function displayFinishedGamesLastDay () {
    let today = Date.now();
    let oneDayAgo = today - (1 * 24 * 60 * 60 * 1000);

    let gamesInTheLastDay = games.filter((game) => game.date < today && game.date > oneDayAgo && game.status == 'completed')
    let gameElements = gamesInTheLastDay.map((game) => displayFinishedGame(game) )
    return gameElements;
  }

  function displayUpcomingGames (days) {
    if (!seasonData.upcomingGames) {
      return <p>Waiting for games to be added to website...</p>
    }

    let today = Date.now();
    let upcomingDays = today + (days * 24 * 60 * 60 * 1000);

    let upcomingGames = seasonData.upcomingGames.filter((game) => game.date > today && game.date < upcomingDays)

    if (upcomingGames.length == 0) {
      return <p>No upcoming games in the next {days > 1 ? `${days} days` : 'day' }</p>
    }
    let sortedUpcomingGames = upcomingGames.sort((a, b) => (a.date < b.date) ? -1 : 1)

    let gameElements = sortedUpcomingGames.map((game, place) => displayUpcomingGame(game) )
    return gameElements;
  }

  function displayFinishedGame (game) {
    return (
      <GameContainer games={games} teams={teams} gameID={game.id}></GameContainer>
    )
  }

  function displayPlayoffGame (game) {
    let gameTBD = false;

    let awayTeam;
    let homeTeam;
    if (game.awayTeamID) {
      awayTeam = utils.getTeamForID(teams, game.awayTeamID);
    } else {
      const placeholderName = game.awayTeamPlaceholder
      awayTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
      gameTBD = true;
    }

    if (game.homeTeamID) {
      homeTeam = utils.getTeamForID(teams, game.homeTeamID);
    } else {
      const placeholderName = game.homeTeamPlaceholder
      homeTeam = {
        seed: '',
        name: placeholderName || 'TBD'
      };
      gameTBD = true;
    }

    let hours = game.date.getHours();
    let hoursLabel = 'AM'
    if(hours >= 12){
      hoursLabel = 'PM';
    }

    if(hours > 12){
      hours -= 12;
    }
    if(hours == 0){
      hours = 12;
    }

    let today = Date.now();


    return (
      <GameContainer games={games} teams={teams} gameID={game.id}></GameContainer>
    )
  }

  function displayPlayoffGames (round) {
    // let games = []
    // let rankedTeams = league.teams
    // let totalTeams = rankedTeams.length

    // let roundNameMap = {
    //   '4': 'quarterfinal',
    //   '2': 'semifinal',
    //   '1': 'championship'
    // }
    // let roundName = roundNameMap[numberOfGames.toString()]

    let gamesThisRound = games.filter((game) => game.type == 'playoff' && game.playoffTags && game.playoffTags.includes(round) && game.season == SEASON);

    let sortedGames = gamesThisRound.sort(function(a,b) {
      let orderA = a.playoffRoundOrder || 0;
      let orderB = b.playoffRoundOrder || 0;
      if (orderA < orderB) {
        return -1;
      } else if (orderB < orderA) {
        return 1;
      } else {
        return 0;
      }
    })

    // let gamesThisRoundWithTeams = gamesThisRound.filter((game) => game.awayTeamID !== '' && game.homeTeamID !== '')
    // let gamesThisRoundWithoutTeams = gamesThisRound.filter((game) => game.awayTeamID == '' || game.homeTeamID == '')
    let gameElements = sortedGames.map((game, place) => displayPlayoffGame(game))
    return (
      <>
        {gameElements}
      </>
    );
  }

  function swipePlayoffRound (swipeDirection, rounds) {
    let playinButton = document.getElementById('playinButton');
    let firstroundButton = document.getElementById('firstroundButton');
    let quarterfinalsButton = document.getElementById('quarterfinalsButton');
    let semifinalsButton = document.getElementById('semifinalsButton');
    let championshipButton = document.getElementById('championshipButton');

    let roundButtons = [playinButton, firstroundButton, quarterfinalsButton, semifinalsButton, championshipButton]

    for (let i = 0; i < roundButtons.length; i++) {
      let roundButton = roundButtons[i]
      if (roundButton.classList.contains('btn-dark')){
        if (swipeDirection == 'right' && i >= roundButtons.length - 1) {
        } else if (swipeDirection == 'left' && i <= roundButtons.length - rounds) {
        } else if (swipeDirection == 'left') {
          roundButtons[i-1].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
          roundButtons[i-1].click();
        } else if (swipeDirection == 'right') {
          roundButtons[i+1].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
          roundButtons[i+1].click();
        }
        break;
      }
    }
  }

  function checkDirectionOfSwipe (rounds) {
    if (Math.abs(startOfSwipeX - endOfSwipeX) < 10) {
      return;
    }
    if (Math.abs(startOfSwipeX - endOfSwipeX) < Math.abs(startOfSwipeY - endOfSwipeY)) {
      return;
    }

    if (endOfSwipeX < startOfSwipeX) {
      swipePlayoffRound('right', rounds)
    } else if (endOfSwipeX > startOfSwipeX) {
      swipePlayoffRound('left', rounds)
    }
  }

  function displayPlayoffRounds () {
    let divClass = '';
    if (SEASON == 1){
      return (
        <div className='league-bracket round_3_of_3' onTouchStart={(e) => {
          startOfSwipeX = e.changedTouches[0].screenX;
          startOfSwipeY = e.changedTouches[0].screenY;
        }} onTouchEnd={(e) => {
          endOfSwipeX = e.changedTouches[0].screenX;
          endOfSwipeY = e.changedTouches[0].screenY;
          let rounds = 3
          checkDirectionOfSwipe(rounds)
        }}>
          { displayPlayoffsForRounds(['quarterfinals', 'semifinals', 'championship']) }
        </div>
      );
    } if (SEASON == 2){
      return (
        <div className={ `league-bracket ${playoffRoundClasses[CURRENT_ROUND]}` } onTouchStart={(e) => {
          startOfSwipeX = e.changedTouches[0].screenX;
          startOfSwipeY = e.changedTouches[0].screenY;
        }} onTouchEnd={(e) => {
          endOfSwipeX = e.changedTouches[0].screenX;
          endOfSwipeY = e.changedTouches[0].screenY;
          let rounds = 5
          checkDirectionOfSwipe(rounds)
        }}>
          { displayPlayoffsForRounds(['playin', 'firstround', 'quarterfinals', 'semifinals', 'championship']) }
        </div>
      );
    }
  }

  function displayPlayoffsForRounds(rounds) {
    let playoffBracket = rounds.map((round) => displayPlayoffRound(round))

    return (
      <>
        { playoffBracket }
      </>
    );
  }

  function displayPlayoffRound(round) {
    let roundNameMap = {
      'playin': 'Play-In',
      'firstround': 'First Round',
      'quarterfinals': 'Quarterfinals',
      'semifinals': 'Semifinals',
      'championship': 'Championship'
    }
    const roundName = roundNameMap[round]

    let roundLocation = 'South Boston Catholic Academy'

    if (SEASON == 1) {
      let roundLocationMap = {
        'quarterfinals': 'South Boston Catholic Academy',
        'semifinals': 'Joseph P. Tynan School',
        'championship': 'South Boston Catholic Academy'
      }

      roundLocation = roundLocationMap[round]
    } else if (SEASON == 2) {
      let roundLocationMap = {
        'playin': 'South Boston Catholic Academy',
        'firstround': 'South Boston Catholic Academy',
        'quarterfinals': 'South Boston Catholic Academy',
        'semifinals': 'South Boston Catholic Academy',
        'championship': 'South Boston Catholic Academy'
      }

      roundLocation = roundLocationMap[round]
    }

    return (
      <div className='league-bracket-round'>
        <h4 className='text-align-center'>{ roundName }</h4>
        <p className='text-align-center'>{ roundLocation }</p>
        <div className='league-bracket-round-games'>
          { displayPlayoffGames(round) }
        </div>
      </div>
    )
  }

  function showAverageStatRow(leader) {
    let statLabelAbbv = {
      'assists': 'APG',
      'points': 'PPG',
      'rebounds': 'RPG',
      'steals': 'SPG',
      'blocks': 'BPG',
    }

    return (
      <div className='league-game-player-stat-group' key={leader.id + '-leader-' + leader.stat }>
        <p className='league-game-player-name'><a className="unstyled-link" href={"/players/" + leader.id }>{ leader.name }</a> <span className='league-season-average-label'>({ leader.teamName })</span></p>
        <p className='league-game-player-stat'>{ parseFloat(leader.value.toFixed(1)) } <span className='league-season-average-label'>{ statLabelAbbv[leader.stat] }</span></p>
      </div>
    )
  }

  function showTopSeasonStats (stat, season = 0) {
    let statLabel = {
      'assists': 'Assists',
      'points': 'Points',
      'rebounds': 'Rebounds',
      'steals': 'Steals',
      'blocks': 'Blocks',
    }

    const statLeaders = seasonData.leaders.filter((leader) => leader.stat == stat);

    if (statLeaders.length == 0) {
      return;
    } else {
      return (
        <div className='league-player-display'>
          <p className="league-title-standard-small text-center">{ statLabel[stat] }</p>
          <div className='league-player-container'>
            <div className='league-game-player-stats'>
              { statLeaders.map((leader) => showAverageStatRow(leader) ) }
            </div>
          </div>
        </div>
      )
    }
  }

  function showStandings (teams) {
    return (
      <div className="league-table-container scroll-on-overflow">
        <table className="table league-table">
          <thead className="league-table-heading-hole">
            <tr>
              <th className="league-table-heading-name"><span>TEAM</span></th>
              <th className="league-table-heading hole"><span>W</span></th>
              <th className="league-table-heading hole"><span>L</span></th>
              <th className="league-table-heading hole"><span>PCT</span></th>
              <th className="league-table-heading hole"><span>PF</span></th>
              <th className="league-table-heading hole"><span>PA</span></th>
              <th className="league-table-heading hole"><span>+/-</span></th>
              <th className="league-table-heading hole"><span>Strk</span></th>
            </tr>
          </thead>
          <thead className="league-table-heading-total">
            <tr>
              <th className="league-table-heading-name"><span>TEAM</span></th>
              <th className="league-table-heading-small" style={{minWidth: '55px'}}><span>W - L</span></th>
              <th className="league-table-heading-small"><span>PCT</span></th>
            </tr>
          </thead>
          <tbody>
            { displayTeams(teams) }
          </tbody>
        </table>
      </div>
    )
  }

  function displayOverallStandings () {
    return (
      <div>
        <p className="league-title-standard-secondary text-align-center">League Morning Star</p>
        { showStandings(seasonData.teams) }
      </div>
    )
  }

  function displayConferenceStandings (conference) {
    let conferenceTeams = seasonData.teams.filter((team) => team.conference == conference)
    let conferenceTitle = conference == 'east' ? 'Eastern Conference' : 'Western Conference'

    return (
      <div>
        <p className="league-title-standard-secondary text-align-center">{ conferenceTitle }</p>
        { showStandings(conferenceTeams) }
      </div>
    )
  }

  function toggleToShowLeagueStandings () {
    setShowOverallStandings(true);
  }
  function toggleToShowConferenceStandings () {
    setShowOverallStandings(false);
  }

  if (dataReady == false) {
    return (
      <div className="Homepage">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="Homepage">
        <div className="league-content">
          <Announcement />
          
          <div className="league-header">
            <div className="league-header-top">
              <div className="league-header-logo">
                <img className="league-logo" src="league.png"></img>
                <a className="btn btn-secondary" href="https://www.gateofheavenstbrigid.org/#MassTimes">🗓️ Mass Times</a>
                <img className="league-logo" src="medal.png"></img>
              </div>
              <p className="league-type-secondary"></p>
            </div>
          </div>

          <div className="league-body">
            <div style={{margin: '10px', textAlign: 'center'}}>
              <a className="league-type" style={{textTransform: 'math-auto'}} href="/our-story">The Story of League Morning Star</a>
            </div>
            <a className="btn league-btn-primary" href='prayer-intentions/new'>🙏 Write a Prayer Intention</a>
          </div>

          { anyGamesInLastDay() ?
            <div className="league-body">
              <p className="league-title-standard-secondary text-align-center">Recent Scores</p>
              <div className="schedule">
                { displayFinishedGamesLastDay() }
              </div>
            </div> : <></>
          }

          <div className="league-body">
            { config.showSignUps ?
            <>
            <p className="league-title-standard-secondary text-align-center">Sign up for Season {utils.toRomanNumeral(parseInt(SEASON))}</p>
            <p>{ config.signUpText }</p>
            <a type="button" className="btn btn-success" href='signup'>Click here to sign up!</a>
            <br></br>
            </>
            :
            <></>
            }

            { config.showDraftBoard &&
              <>
                <p className="league-title-standard-secondary text-align-center">Season {utils.toRomanNumeral(parseInt(SEASON))} Draft</p>
                <p>Teams are being formed for the upcoming season. Check out the draft board to see which players have been selected.</p>
                <a type="button" className="btn league-btn-primary" href='/draft'>📋 View Draft Board</a>
                <br></br>
              </>
            }

            { games.length > 0 &&
              (config.showPlayoffBracket ?
              <>
                <p className="league-title-standard-secondary text-align-center">Upcoming Games</p>
                <a className="league-type league-mini-header-link" href="/schedule">View All Games</a>
                <div className="league-table-container">
                  { displayUpcomingGames(3) }
                </div>
                <Bracket games={games} teams={seasonTeams} isCurrentSeason={true} showPlaceholders={ !config.showPlayoffBracket }></Bracket>
              </>
              :
              <>
                <p className="league-title-standard-secondary text-align-center">Upcoming Games</p>
                <a className="league-type league-mini-header-link" href="/schedule">View All Games</a>
                <div className="league-table-container">
                  { displayUpcomingGames(3) }
                </div>
              </>)
            }
          </div>

          <div className="league-body">
            { seasonData.teams.length == 0 ?
              <></>
            :
              <>
                <p className="league-title-standard text-align-center">Standings</p>

                { seasonData.teams.some(t => t.conference === 'east' || t.conference === 'west') ? (
                  <>
                    <div className="btn-group league-radio-btn-group" role="group" aria-label="League/conference radio toggle button group">
                      <input type="radio" className="btn-check" name="btnradio" id="btnLeague" autoComplete="off" defaultChecked onClick={ () => { toggleToShowLeagueStandings() }}></input>
                      <label className="btn league-btn-outline-primary" htmlFor="btnLeague">League</label>

                      <input type="radio" className="btn-check" name="btnradio" id="btnConference" autoComplete="off" onClick={ () => { toggleToShowConferenceStandings() }}></input>
                      <label className="btn league-btn-outline-primary" htmlFor="btnConference">Conference</label>
                    </div>

                    { showOverallStandings ?
                      displayOverallStandings() :
                      <>
                        { displayConferenceStandings('west') }
                        { displayConferenceStandings('east') }
                      </>
                    }
                  </>
                ) : (
                  displayOverallStandings()
                )}
              </>
            }

            { seasonData.leaders.length == 0 ?
              <></>
            :
              <>
                { utils.showSeasonLeaders(games, seasonTeams) ?
                <div className="league-game-stats-container">
                  <p className="league-title-skinny-medium"><b>Season Leaders</b></p>
                  <a className="league-type league-mini-header-link" href="/records">All-time Records</a>
                  { showTopSeasonStats('points', SEASON) }
                  { showTopSeasonStats('rebounds', SEASON) }
                  { showTopSeasonStats('assists', SEASON) }
                  { showTopSeasonStats('steals', SEASON) }
                  { showTopSeasonStats('blocks', SEASON) }
                </div> : ''
                }
              </>
            }
          </div>

          <div className="league-mini-link-container">
            <a className="league-type league-mini-header-link" href="/stats">Single-Game Records</a>
          </div>

          <div className="league-mini-link-container">
            <a className="league-type league-mini-header-link" href="/records">All-time Records</a>
          </div>

          <div style={{margin: '10px', textAlign: 'center'}}>
            <a type="button" className="btn btn-success" href={`seasons/${utils.getLastSeason(SEASON)}`}>{ `📊 Results from Season ${utils.getLastSeason(SEASON)}` }</a>
          </div>
        </div>
        <Footer/>
      </div>
    );
  }
}

export default Homepage;
